import { SPARKY_SERVICE_URL_PROVIDER_EXTPOINT, type SparkyServiceUrlProviderConfig } from '../extpoints'
import { API_SERVICE_URLS_NAME, PAAS_REDIRECT_URL_NAME, ADMIN_CLUSTER_URL_NAME, SPIFFY_URL_KEY, FAWKES_APP_ID, FAWKES_APP_IDS, RUNNING, AIOPS_URL_KEY, PROP_AIOPS_REGION_INFO, AIOPS_FREE_APP_ID, GOV_REGION, API_GATEWAY_URL_KEY, API_GATEWAY_GOV_URL_KEY } from '../framework/constants'
import { getRawServiceUrlMappingOrString } from '../framework/states'
import { isString } from '../framework/utils'
import type { TsgInstances } from '../framework/indexedArrays'
import type { ServiceOptions, StoreState } from '../framework/stateTypes'
import type { ExtensionsConfig } from '../framework/microAppTypes'

const getServiceUrlProviderExt = () => {
  const getFawkesInst = (instances: TsgInstances | undefined, isMsp?: boolean) => {
    if (instances?.length) {
      const fawkesAppIds = isMsp ? FAWKES_APP_IDS : [FAWKES_APP_ID]
      for (const appId of fawkesAppIds) {
        const inst = instances.get(appId)
        if (inst?.status === RUNNING && inst.runtime_attributes) { // already handled the hidden case when indexing
          return inst
        }
      }
    }
  }
  const ensureHttpsOrigin = (fqdn?: string) => {
    if (!fqdn) {
      return fqdn
    }
    const origin = fqdn.startsWith('https:') ? fqdn : `https://${fqdn}`
    try {
      const url = new URL(origin)
      return url.origin
    }
    catch (e) {
      console.error('bad url', origin, e)
      return origin?.endsWith('/') ? origin.slice(0, -1) : origin
    }
  }
  const getRegionalMappingUrl = (name: string, options: ServiceOptions, state: StoreState): string | undefined => {
    const urls = state.fxVars[API_SERVICE_URLS_NAME]?.[name]
    if (!urls) {
      return
    }
    if (isString(urls)) {
      return urls
    }
    const mappingKeyValue = options?.mappingKey && urls[options.mappingKey]
    if (mappingKeyValue) {
      return mappingKeyValue
    }
    const region = options?.region || state.auth?.instances?.platform_region
    return (region && urls[region]) || urls.default || undefined
  }
  return {
    point: SPARKY_SERVICE_URL_PROVIDER_EXTPOINT,
    configs: [{
      id: 'sparky.service.url.fawkes.paas_redirect_url',
      config: {
        match: PAAS_REDIRECT_URL_NAME,
        allowFallback: false, // so getServiceUrl('paas_redirect_url') will not ends with /
        getServiceUrl: (_name, _options, state) => {
          const fawkesInst = getFawkesInst(state.auth?.instances, state.fxVars.msp)
          const paasRedirectUrl = fawkesInst?.runtime_attributes?.paas_api_url
          if (paasRedirectUrl) {
            return ensureHttpsOrigin(paasRedirectUrl)
          }
        }
      }
    }, {
      id: 'sparky.service.url.fawkes.admin_cluster_url',
      config: {
        match: ADMIN_CLUSTER_URL_NAME,
        allowFallback: true,
        getServiceUrl: (_name, _options, state) => {
          const fawkesInst = getFawkesInst(state.auth?.instances, state.fxVars.msp)
          const adminUrl = fawkesInst?.runtime_attributes?.admin_api_url
          if (adminUrl) {
            return ensureHttpsOrigin(adminUrl)
          }
          const defaultUrl = state.fxVars[ADMIN_CLUSTER_URL_NAME] ||
            state.fxVars[API_SERVICE_URLS_NAME]?.default_admin_cluster_url
          return ensureHttpsOrigin(defaultUrl)
        }
      }
    }, {
      id: 'sparky.service.url.fawkes.spiffy',
      config: {
        match: SPIFFY_URL_KEY,
        allowFallback: false,
        getServiceUrl: (_name, options, state) => {
          const fawkesInst = getFawkesInst(state.auth?.instances, state.fxVars.msp)
          const spiffyUrl = fawkesInst?.runtime_attributes?.spiffy_api_url
          if (spiffyUrl) {
            return ensureHttpsOrigin(spiffyUrl)
          }
          const mappedUrl = getRegionalMappingUrl(SPIFFY_URL_KEY, options, state)
          return ensureHttpsOrigin(mappedUrl)
        }
      }
    }, {
      id: 'sparky.service.url.api_gw_tsg',
      config: {
        match: ['apigw_curtsg', 'api_gateway_current_tsg'],
        allowFallback: false,
        getServiceUrl: (_name, _options, { auth }) => {
          const isGov = auth?.instances?.platform_region === GOV_REGION
          if (isGov) {
            const url = getRawServiceUrlMappingOrString(API_GATEWAY_GOV_URL_KEY)
            if (url) {
              return url
            }
          } // fallback to normal api gw if gov one is not provided
          return getRawServiceUrlMappingOrString(API_GATEWAY_URL_KEY)
        }
      }
    }, {
      id: 'sparky.service.url.aiops.api',
      config: {
        match: [AIOPS_URL_KEY, `${AIOPS_URL_KEY}_api`, `${AIOPS_URL_KEY}_apigw`],
        allowFallback: false,
        getServiceUrl: (name, _options, state) => {
          const key = name.endsWith('apigw') ? 'apigw_api_origin' : 'aiops_api_origin'
          const { properties, instances } = state.auth
          const regionInfo = properties?.get(PROP_AIOPS_REGION_INFO)?.aiops_region
          if (regionInfo) {
            if (regionInfo.api_origins?.[key]) {
              return ensureHttpsOrigin(regionInfo.api_origins[key])
            }
            const originSet = new Set()
            for (const tenantId of Object.keys(regionInfo)) {
              const info = regionInfo[tenantId] as { api_origins?: Record<string, string> }
              const origin = info?.api_origins?.[key]
              if (!origin) {
                continue
              }
              if (state.fxVars.aiops_free) {
                const appId = instances?.get(tenantId)?.app_id
                if (appId === AIOPS_FREE_APP_ID) {
                  return ensureHttpsOrigin(origin) || undefined
                }
                continue
              }
              originSet.add(ensureHttpsOrigin(origin))
            }
            const origins = Array.from(originSet)
            if (!origins.length) {
              return undefined
            }
            else if (origins.length > 1) {
              console.error('Multiple AIOps API origins found, using the first one:', origins)
            }
            return origins[0]
          }
          return undefined // TODO: if there should be a fallback default origin or regional mapping?
        }
      }
    }]
  } as ExtensionsConfig<SparkyServiceUrlProviderConfig>
}

export default getServiceUrlProviderExt()
